import type { JSONContent } from "@tiptap/core";
import Mention from "@tiptap/extension-mention";
import Placeholder from "@tiptap/extension-placeholder";
import { CharacterCount } from "@tiptap/extensions";
import {
  type Editor,
  EditorContent,
  ReactRenderer,
  useEditor,
} from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { useImperativeHandle, useMemo, useState } from "react";
import tippy, { type Instance as TippyInstance } from "tippy.js";
import { cn } from "../lib/utils";
import MentionList, { type MentionListHandle } from "./mention-list";

export interface MentionableItem {
  id: string;
  name: string;
  description: string;
}

export type TextEditorProps = {
  initialContent?: JSONContent;
  onContentChange?: (jsonContent: JSONContent, editor: Editor) => void;
  readOnly?: boolean;
  canMention?: boolean;
  className?: string;
  mentionClassName?: string;
  mentionTriggerChar?: string;
  noResultsMessage?: string;
  placeholder?: string;
  error?: boolean;
  disabled?: boolean;
  mentionableItems?: MentionableItem[];
  maxLength?: number;
};

export type TextEditorHandle = {
  reset: () => void;
};

export const TextEditor = (
  props: TextEditorProps & { ref?: React.Ref<TextEditorHandle> },
) => {
  const {
    initialContent,
    onContentChange,
    readOnly = false,
    canMention = true,
    className = "",
    mentionClassName = "",
    mentionTriggerChar = "@",
    noResultsMessage,
    placeholder = "Type something...",
    error = false,
    disabled = false,
    mentionableItems,
    maxLength,
    ref,
  } = props;
  const [isFocused, setIsFocused] = useState(false);

  const extensions = useMemo(
    () => [
      StarterKit,
      CharacterCount.configure({
        limit: maxLength,
      }),
      Placeholder.configure({
        placeholder: placeholder,
        showOnlyWhenEditable: true,
        includeChildren: true,
        emptyEditorClass: cn(
          "before:content-[attr(data-placeholder)] before:float-left before:text-gray-600 before:h-0 before:pointer-events-none bg-white !text-caption",
          error ? "border-red-500" : "border-gray-200",
          !disabled && !error
            ? "hover:border-pink-200 focus-within:border-pink-300"
            : "",
          disabled
            ? [
                "cursor-not-allowed",
                "bg-gray-50 text-gray-700 placeholder:text-gray-500 border-gray-200",
              ]
            : "",
        ),
      }),
      Mention.configure({
        HTMLAttributes: {
          class: cn("text-pink-500 font-semibold", mentionClassName),
        },
        suggestion: {
          char: mentionTriggerChar,
          items: canMention
            ? ({ query }) => {
                if (!mentionableItems) return [];
                return mentionableItems.filter((item) =>
                  item.name.toLowerCase().includes(query.toLowerCase()),
                );
              }
            : () => [],
          render: canMention
            ? () => {
                let component: ReactRenderer<MentionListHandle>;
                let popup: TippyInstance;

                return {
                  onStart: async (props) => {
                    component = new ReactRenderer(MentionList, {
                      props: {
                        ...props,
                        noResultsMessage: noResultsMessage,
                      },
                      editor: props.editor,
                    });

                    if (!props.clientRect) return;

                    popup = tippy(document.body, {
                      getReferenceClientRect: () =>
                        props.clientRect?.() || new DOMRect(),
                      appendTo: () => document.body,
                      content: component.element,
                      showOnCreate: true,
                      interactive: true,
                      trigger: "manual",
                      placement: "bottom-start",
                    });
                  },
                  onUpdate(props) {
                    component?.updateProps(props);

                    if (!props.clientRect) return;

                    popup?.setProps({
                      getReferenceClientRect: () =>
                        props.clientRect?.() || new DOMRect(),
                    });
                  },
                  onKeyDown(props) {
                    if (props.event.key === "Escape") {
                      popup?.hide();
                      return true;
                    }

                    return component?.ref?.onKeyDown(props) || false;
                  },
                  onExit() {
                    popup?.destroy();
                    component?.destroy();
                  },
                };
              }
            : undefined,
        },
      }),
    ],
    [
      maxLength,
      placeholder,
      mentionClassName,
      mentionTriggerChar,
      canMention,
      mentionableItems,
      noResultsMessage,
      disabled,
      error,
    ],
  );

  const editor = useEditor({
    extensions,
    editable: !readOnly && !disabled,
    content: initialContent,
    onUpdate: ({ editor }) => {
      const jsonContent = editor.getJSON();
      onContentChange?.(jsonContent, editor);
    },
    onFocus: () => setIsFocused(true),
    onBlur: () => setIsFocused(false),
  });

  const reset = () => {
    if (!editor) {
      return;
    }

    if (initialContent) {
      editor.commands.setContent(initialContent);
    } else {
      editor.commands.clearContent();
    }
  };

  useImperativeHandle(ref, () => ({
    reset,
  }));

  if (!editor) {
    return null;
  }

  return (
    <div className="flex flex-col space-y-1">
      <div
        className={cn(
          "relative flex flex-col border rounded bg-transparent transition-colors bg-white",
          error ? "border-red-500" : "border-gray-200",
          !disabled && !error
            ? "hover:border-pink-200 focus-within:border-pink-300"
            : "",
          disabled
            ? [
                "cursor-not-allowed",
                "bg-gray-50 text-gray-700 placeholder:text-gray-500 border-gray-200",
              ]
            : "",
          readOnly && "bg-transparent border-none",
          className,
        )}
      >
        <EditorContent
          editor={editor}
          className={cn(
            "px-4 py-2 text-caption pink-scroll",
            readOnly && "text-body break-words p-0",
          )}
          style={{
            height: readOnly
              ? "auto"
              : isFocused || editor.getText().trim().length > 0
                ? "200px"
                : "64px",
            transition: readOnly ? undefined : "height 0.3s ease",
            overflowY: "auto",
          }}
        />
      </div>
    </div>
  );
};
